import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { router, useFocusEffect } from "expo-router";
import { useDispatch, useSelector } from "react-redux";
import { Toast } from "react-native-toast-notifications";
import i18n from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import WebSidebar from "./WebSidebar";
import { RootState } from "../../store";
import { authActions } from "../../store/slices/authSlice";
import { appActions } from "../../store/slices/appSlice";

// Import settings image for header
import SettingsImage from "../../assets/images/settings.png";

const WebProfileScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { user, loading, refetch } = useSelector(
    (state: RootState) => state.auth,
  );
  const { locale } = useSelector((state: RootState) => state.app);

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  // State for fields
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);

  const userData = user as {
    username?: string;
    email?: string;
    phone?: string;
    is_verified?: boolean;
    created_at?: string;
    payed?: boolean;
    photo?: string;
  } | null;

  useEffect(() => {
    if (userData) {
      setUsername(userData.username || "");
      setPhone(userData.phone || "");
      setEmail(userData.email || "");
    }
  }, [userData]);

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch]),
  );

  // Fetch user data on mount and when refetch changes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await customAxios.get("/users/check");
        dispatch(authActions.setUser(res.data.userinfo));
      } catch (error) {
        // silently ignore
      }
    };
    fetchUserData();
  }, [dispatch, refetch]);

  const handleGoBack = () => {
    router.push("/(drawer)/params" as any);
  };

  const handleChangePassword = () => {
    router.push("/(drawer)/params/profile/change-password" as any);
  };

  const handleViewLimits = () => {
    router.push("/(drawer)/params/limits" as any);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  // Cancel edit mode and reset form fields
  const handleCancelEdit = () => {
    if (userData) {
      setUsername(userData.username || "");
      setPhone(userData.phone || "");
      setEmail(userData.email || "");
    }
    setIsEditing(false);
  };

  // Handle profile photo change (web file picker)
  const handleChangePhoto = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file size (max 5MB before compression)
      if (file.size > 5 * 1024 * 1024) {
        Toast.show(i18n.t("profilePhotoFailed"), { type: "danger" });
        return;
      }

      setPhotoUploading(true);
      try {
        const formData = new FormData();
        formData.append("photo", file);

        await customAxios.patch("/users/update", formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000,
        });

        Toast.show(i18n.t("profilePhotoUpdated"), { type: "success" });
        dispatch(authActions.toggleRefetch());
      } catch (error: any) {
        Toast.show(i18n.t("profilePhotoFailed"), { type: "danger" });
      } finally {
        setPhotoUploading(false);
      }
    };
    input.click();
  };

  // Save profile data
  const handleSaveProfile = async () => {
    setSaveLoading(true);
    try {
      // Basic validation
      if (!username.trim()) {
        Toast.show(i18n.t("usernameRequired"), {
          type: "danger",
        });
        setSaveLoading(false);
        return;
      }

      // Phone validation (allow 10 digits with optional spaces/dashes)
      if (phone && !/^\d{10}$/.test(phone.replace(/[\s-]/g, ""))) {
        Toast.show(i18n.t("invalidPhoneNumber"), {
          type: "danger",
        });
        setSaveLoading(false);
        return;
      }

      // Email validation
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        Toast.show(i18n.t("invalidEmail"), {
          type: "danger",
        });
        setSaveLoading(false);
        return;
      }

      // Only include fields that have actually changed
      const updatedFields: any = {};

      if (email !== (userData?.email || "")) {
        updatedFields.email = email;
      }
      if (username !== (userData?.username || "")) {
        updatedFields.username = username;
      }
      if (phone !== (userData?.phone || "")) {
        updatedFields.phone = phone;
      }

      // If no fields changed, show a message and return
      if (Object.keys(updatedFields).length === 0) {
        Toast.show(i18n.t("noChangesToSave"), {
          type: "info",
        });
        setSaveLoading(false);
        return;
      }

      await customAxios.patch(`/users/update`, updatedFields, {
        withCredentials: true,
      });

      Toast.show(i18n.t("profileUpdateSuccess"), { type: "success" });

      // Refetch user data to get updated info
      dispatch(authActions.toggleRefetch());

      // Exit edit mode after successful save
      setIsEditing(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      if (errorMessage) {
        Toast.show(errorMessage, { type: "danger" });
      } else {
        Toast.show(i18n.t("profileUpdateFailed"), {
          type: "danger",
        });
      }
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div
      key={`profile-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with breadcrumb and blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={SettingsImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.breadcrumb}>
                <TouchableOpacity onPress={handleGoBack}>
                  <Text style={styles.breadcrumbLink}>
                    {i18n.t("settings")}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSeparator}>{"  >  "}</Text>
                <Text style={styles.breadcrumbCurrent}>
                  {i18n.t("comptepersonel")}
                </Text>
              </View>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Profile Photo Section */}
          <View style={styles.profilePhotoSection}>
            <TouchableOpacity
              onPress={handleChangePhoto}
              disabled={photoUploading}
              activeOpacity={0.7}
              style={styles.profilePhotoWrapper}
            >
              <View style={styles.profilePhotoContainer}>
                {photoUploading ? (
                  <ActivityIndicator size="large" color="#0596D0" />
                ) : userData?.photo ? (
                  <Image
                    source={{
                      uri: userData.photo.startsWith("http")
                        ? userData.photo
                        : `https://api.khdame.com${userData.photo}`,
                    }}
                    style={styles.profilePhoto}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={styles.profilePhotoFallback}>
                    <Text style={{ fontSize: 40 }}>👤</Text>
                  </View>
                )}
              </View>
              {/* Camera badge */}
              <View style={styles.cameraBadge}>
                <Text style={{ fontSize: 14, color: "white" }}>📷</Text>
              </View>
            </TouchableOpacity>
            <Text style={styles.changePhotoText}>
              {i18n.t("changeProfilePhoto")}
            </Text>
          </View>

          {/* Edit/Save Button Bar */}
          <View style={styles.editButtonBar}>
            {!isEditing ? (
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => setIsEditing(true)}
              >
                <Text style={styles.editButtonIcon}>✏️</Text>
                <Text style={styles.editButtonText}>
                  {i18n.t("updateProfile")}
                </Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.editButtonRow}>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={handleCancelEdit}
                >
                  <Text style={styles.cancelButtonText}>
                    {i18n.t("cancel")}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.saveButton}
                  onPress={handleSaveProfile}
                  disabled={saveLoading}
                >
                  {saveLoading ? (
                    <ActivityIndicator size="small" color="#FFF" />
                  ) : (
                    <>
                      <Text style={styles.saveButtonIcon}>💾</Text>
                      <Text style={styles.saveButtonText}>
                        {i18n.t("save")}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Profile Form */}
          <View style={styles.formContainer}>
            {/* Username Field */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{i18n.t("username")}</Text>
              <View
                style={[
                  styles.fieldInputContainer,
                  isEditing && styles.fieldInputEditing,
                ]}
              >
                <Text style={styles.fieldIcon}>👤</Text>
                {isEditing ? (
                  <TextInput
                    style={styles.fieldTextInput}
                    value={username}
                    onChangeText={setUsername}
                    placeholder={i18n.t("defaultUsername")}
                    placeholderTextColor="#9CA3AF"
                  />
                ) : (
                  <Text style={styles.fieldValue}>
                    {username || i18n.t("defaultUsername")}
                  </Text>
                )}
              </View>
            </View>

            {/* Email Field */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{i18n.t("email")}</Text>
              <View
                style={[
                  styles.fieldInputContainer,
                  isEditing && styles.fieldInputEditing,
                ]}
              >
                <Text style={styles.fieldIcon}>✉️</Text>
                {isEditing ? (
                  <TextInput
                    style={styles.fieldTextInput}
                    value={email}
                    onChangeText={setEmail}
                    placeholder="email@example.com"
                    placeholderTextColor="#9CA3AF"
                    keyboardType="email-address"
                  />
                ) : (
                  <Text style={styles.fieldValue}>
                    {email || "store.email@gmail.com"}
                  </Text>
                )}
              </View>
            </View>

            {/* Phone Field */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{i18n.t("phone")}</Text>
              <View
                style={[
                  styles.fieldInputContainer,
                  isEditing && styles.fieldInputEditing,
                ]}
              >
                <Text style={styles.fieldIcon}>📞</Text>
                {isEditing ? (
                  <TextInput
                    style={styles.fieldTextInput}
                    value={phone}
                    onChangeText={setPhone}
                    placeholder="0540849366"
                    placeholderTextColor="#9CA3AF"
                    keyboardType="phone-pad"
                  />
                ) : (
                  <Text style={styles.fieldValue}>
                    {phone || i18n.t("notSpecified")}
                  </Text>
                )}
              </View>
            </View>

            {/* Subscription Row */}
            <View style={styles.subscriptionRow}>
              <View style={styles.subscriptionItem}>
                <Text style={styles.subscriptionLabel}>
                  {i18n.t("subscriptionType")}
                </Text>
                <View style={styles.subscriptionValueContainer}>
                  <Text style={styles.subscriptionValue}>
                    {userData?.payed ? i18n.t("premium") : i18n.t("free")}
                  </Text>
                </View>
              </View>

              <View style={styles.subscriptionItem}>
                <Text style={styles.subscriptionLabel}>
                  {i18n.t("subscriptionDate")}
                </Text>
                <View style={styles.subscriptionValueContainer}>
                  <Text style={styles.subscriptionValue}>
                    {formatDate(userData?.created_at)}
                  </Text>
                </View>
              </View>

              <TouchableOpacity style={styles.upgradeButton}>
                <Text style={styles.upgradeButtonArrow}>‹</Text>
                <Text style={styles.upgradeButtonText}>
                  {i18n.t("activateAnnual")}
                </Text>
                <Text style={styles.upgradeButtonIcon}>⭐</Text>
              </TouchableOpacity>
            </View>

            {/* Action Buttons Row */}
            <View style={styles.actionButtonsRow}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleViewLimits}
              >
                <Text style={styles.actionButtonArrow}>‹</Text>
                <View style={styles.actionButtonContent}>
                  <Text style={styles.actionButtonText}>
                    {i18n.t("viewFreeLimits")}
                  </Text>
                  <Text style={styles.actionButtonIcon}>📋</Text>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleChangePassword}
              >
                <Text style={styles.actionButtonArrow}>‹</Text>
                <View style={styles.actionButtonContent}>
                  <Text style={styles.actionButtonText}>
                    {i18n.t("changePassword")}
                  </Text>
                  <Text style={styles.actionButtonIcon}>🔒</Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  profilePhotoSection: {
    alignItems: "center",
    paddingVertical: 20,
    marginBottom: 10,
  },
  profilePhotoWrapper: {
    position: "relative" as any,
    cursor: "pointer" as any,
  },
  profilePhotoContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "white",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    // @ts-ignore
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    overflow: "hidden" as any,
  },
  profilePhoto: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  profilePhotoFallback: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#FB923C",
    alignItems: "center",
    justifyContent: "center",
  },
  cameraBadge: {
    position: "absolute" as any,
    bottom: 0,
    right: 0,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#0596D0",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "white",
  },
  changePhotoText: {
    marginTop: 8,
    fontSize: 12,
    color: "#9CA3AF",
  },
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 30,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbLink: {
    fontSize: 28,
    color: "#333",
  },
  breadcrumbSeparator: {
    fontSize: 28,
    color: "#999",
    marginHorizontal: 4,
  },
  breadcrumbCurrent: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  formContainer: {
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 40,
  },
  fieldContainer: {
    width: "100%",
    maxWidth: 600,
  },
  fieldLabel: {
    fontSize: 16,
    color: "#666",
    textAlign: "right",
    marginBottom: 8,
  },
  fieldInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  fieldContentRight: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  fieldIcon: {
    fontSize: 22,
  },
  fieldValue: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right",
  },
  dropdownArrow: {
    fontSize: 20,
    color: "#666",
    marginRight: 12,
  },
  dropdownOptions: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginTop: 8,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  dropdownOption: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  optionSelected: {
    backgroundColor: "#E8F4F8",
  },
  optionText: {
    fontSize: 18,
    color: "#333",
    textAlign: "right",
  },
  subscriptionRow: {
    flexDirection: "row",
    gap: 16,
    width: "100%",
    maxWidth: 600,
    marginTop: 24,
  },
  subscriptionItem: {
    flex: 1,
  },
  subscriptionLabel: {
    fontSize: 14,
    color: "#666",
    textAlign: "center",
    marginBottom: 8,
  },
  subscriptionValueContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  subscriptionValue: {
    fontSize: 16,
    color: "#333",
    fontWeight: "500",
  },
  upgradeButton: {
    flex: 1.5,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#8E36C2",
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: "center",
    gap: 8,
    marginTop: 20,
  },
  upgradeButtonArrow: {
    fontSize: 20,
    color: "#FFFFFF",
  },
  upgradeButtonText: {
    fontSize: 14,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  upgradeButtonIcon: {
    fontSize: 18,
  },
  actionButtonsRow: {
    flexDirection: "row",
    gap: 16,
    width: "100%",
    maxWidth: 600,
    marginTop: 24,
  },
  actionButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  actionButtonArrow: {
    fontSize: 22,
    color: "#666",
    marginRight: 12,
  },
  actionButtonContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  actionButtonText: {
    fontSize: 16,
    color: "#333",
    fontWeight: "500",
  },
  actionButtonIcon: {
    fontSize: 22,
  },
  // Edit mode styles
  editButtonBar: {
    alignItems: "center",
    paddingHorizontal: 40,
    marginBottom: 24,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1379A2",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 32,
    width: "100%",
    maxWidth: 500,
    gap: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  editButtonIcon: {
    fontSize: 20,
  },
  editButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600" as any,
  },
  editButtonRow: {
    flexDirection: "row",
    gap: 16,
    width: "100%",
    maxWidth: 500,
  },
  cancelButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#6B7280",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
  cancelButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600" as any,
  },
  saveButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#22C55E",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 24,
    gap: 8,
  },
  saveButtonIcon: {
    fontSize: 18,
  },
  saveButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600" as any,
  },
  fieldInputEditing: {
    borderWidth: 2,
    borderColor: "#1379A2",
    backgroundColor: "#FFFFFF",
  } as any,
  fieldTextInput: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right" as any,
    paddingVertical: 0,
  },
});

export default WebProfileScreen;
